import { MultiSelect } from '@hubertai/hubert-component-library';
import { Button, Heading, IconButton, Select, Separator, Switch, Text, TextField } from '@radix-ui/themes';
import { Trash2, Upload } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { ChatPreview } from '../components/ChatPreview';
import { EditorField } from '../components/EditorField';
import { useThemeEditor } from '../hooks/useThemeEditor';
import { isValidHex, resolveTextColor } from '../utils/themeUtils';
import type { TTemplate } from 'types/template/template.types';
import { defaultBrandingPrimaryColor } from 'types/branding.types';

type Option = { label: string; value: string };

const baselineKey = (themeId: string | undefined, baseline: string[]) => `${themeId}:${baseline.join(',')}`;

export const ThemeEditorPage = () => {
  const {
    activeTheme,
    deleteTheme,
    updateTheme,
    saveTheme,
    uploadLogo,
    uploadFavicon,
    isSaving,
    templatesData,
    campaignsData,
    paginatedJobsData,
    isLoadingTemplates,
    isLoadingCampaigns,
    isLoadingJobs,
  } = useThemeEditor();

  const templateOptions: Option[] = useMemo(() => {
    const templates = templatesData?.templates;
    if (!templates) return [];
    return [...templates]
      .sort((a, b) => a.name.trim().localeCompare(b.name.trim(), undefined, { numeric: true, sensitivity: 'base' }))
      .map((t: TTemplate) => ({ label: t.name.trim(), value: t._id }));
  }, [templatesData?.templates]);

  const preSelectedIds = useMemo(() => {
    const themeId = activeTheme.id;
    const templates = templatesData?.templates;
    if (!themeId || !templates) return [];
    return templates.filter((t: TTemplate) => t.theme_id === themeId).map((t: TTemplate) => t._id);
  }, [templatesData?.templates, activeTheme.id]);

  const selectedTemplateIds = activeTheme.templateIds ?? preSelectedIds;

  const campaignOptions: Option[] = useMemo(() => {
    if (!campaignsData) return [];
    return [...campaignsData]
      .sort((a, b) => a.name.trim().localeCompare(b.name.trim(), undefined, { numeric: true, sensitivity: 'base' }))
      .map((c) => ({ label: c.name.trim(), value: c.id! }));
  }, [campaignsData]);

  const preSelectedCampaignIds = useMemo(() => {
    const themeId = activeTheme.id;
    if (!themeId || !campaignsData) return [];
    return campaignsData.filter((c) => c.themeId === themeId).map((c) => c.id!);
  }, [campaignsData, activeTheme.id]);

  const selectedCampaignIds = activeTheme.campaignIds ?? preSelectedCampaignIds;

  const jobOptions: Option[] = useMemo(() => {
    const jobs = paginatedJobsData?.data;
    if (!jobs) return [];
    return [...jobs]
      .sort((a, b) =>
        (a.title ?? '').trim().localeCompare((b.title ?? '').trim(), undefined, { numeric: true, sensitivity: 'base' }),
      )
      .map((j) => ({ label: (j.title ?? '').trim(), value: j._id }));
  }, [paginatedJobsData?.data]);

  const preSelectedJobIds = useMemo(() => {
    const themeId = activeTheme.id;
    const jobs = paginatedJobsData?.data;
    if (!themeId || !jobs) return [];
    return jobs.filter((j: { theme_id?: string; _id: string }) => j.theme_id === themeId).map((j) => j._id);
  }, [paginatedJobsData?.data, activeTheme.id]);

  const selectedJobIds = activeTheme.jobIds ?? preSelectedJobIds;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  if (!activeTheme) return null;

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadLogo(file);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFavicon(file);
    }
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-10">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center justify-between">
            <Heading size="6">{activeTheme.name}</Heading>
            <IconButton
              variant="soft"
              color="red"
              onClick={() => deleteTheme(activeTheme.id || 'hubert-default')}
              title="Remove theme"
              className={activeTheme.id === 'hubert-default' ? '' : 'cursor-pointer!'}
              disabled={activeTheme.id === 'hubert-default'}
            >
              <Trash2 size={16} />
            </IconButton>
          </div>

          <Separator className="my-3 w-full!"></Separator>

          <div className="flex flex-col gap-3">
            <EditorField label="Theme Name">
              <TextField.Root
                value={activeTheme.name}
                onChange={(e) => updateTheme({ name: e.target.value })}
                placeholder="e.g. Christmas Campaign"
                size="3"
                disabled={activeTheme.id === 'hubert-default'}
              />
            </EditorField>

            <EditorField label="Agent Name">
              <TextField.Root
                value={activeTheme.agentName}
                onChange={(e) => updateTheme({ agentName: e.target.value })}
                placeholder="e.g. Hubert"
                size="3"
                disabled={activeTheme.id === 'hubert-default'}
              />
            </EditorField>

            <EditorField label="Privacy Policy URL">
              <TextField.Root
                value={activeTheme.policies?.[0]?.url || ''}
                onChange={(e) => updateTheme({ policies: [{ url: e.target.value }] })}
                placeholder="e.g. https://www.hubert.ai/candidate-privacy-policy"
                size="3"
                disabled={activeTheme.id === 'hubert-default'}
                type="url"
              />
            </EditorField>

            {/* Primary Color */}
            <div className="grid grid-cols-2 gap-8">
              <EditorField label="Primary Color">
                <div className="flex h-10 items-center gap-3">
                  <input
                    type="color"
                    disabled={activeTheme.id === 'hubert-default'}
                    value={activeTheme.brandColor || defaultBrandingPrimaryColor}
                    onChange={(e) => updateTheme({ brandColor: e.target.value })}
                    className={`color-picker-borderless h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-gray-200 p-0 shadow-sm ${activeTheme.id === 'hubert-default' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                  <div className="min-w-0 flex-1">
                    <TextField.Root
                      size="3"
                      disabled={activeTheme.id === 'hubert-default'}
                      color={
                        activeTheme.id !== 'hubert-default' && !isValidHex(activeTheme.brandColor || '')
                          ? 'red'
                          : undefined
                      }
                      className="w-full"
                      value={activeTheme.brandColor}
                      onChange={(e) => updateTheme({ brandColor: e.target.value })}
                    />
                  </div>
                </div>
              </EditorField>
              <EditorField label="Text Color">
                <div className="flex h-10 items-center gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 shadow-sm"
                    title="Text color preview"
                    style={{ backgroundColor: activeTheme.brandColor || defaultBrandingPrimaryColor }}
                  >
                    <Text
                      size="2"
                      weight="bold"
                      style={{
                        color: resolveTextColor(
                          activeTheme.textColor as 'auto' | 'dark' | 'light',
                          activeTheme.brandColor || defaultBrandingPrimaryColor,
                        ),
                      }}
                    >
                      Abc
                    </Text>
                  </div>
                  <div className="min-w-0 flex-1">
                    <Select.Root
                      size="3"
                      disabled={activeTheme.id === 'hubert-default'}
                      value={activeTheme.textColor}
                      onValueChange={(val: 'light' | 'dark' | 'auto') => updateTheme({ textColor: val })}
                    >
                      <Select.Trigger className="w-full!" />
                      <Select.Content position="popper">
                        <Select.Item value="auto">Auto</Select.Item>
                        <Select.Item value="light">Light</Select.Item>
                        <Select.Item value="dark">Dark</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </div>
                </div>
              </EditorField>
            </div>

            {/* Color Scheme */}
            <EditorField label="Color Scheme">
              <Select.Root
                size="3"
                disabled
                value={activeTheme.colorScheme}
                onValueChange={(val: 'light' | 'dark') => updateTheme({ colorScheme: val })}
              >
                <Select.Trigger className="w-full!" />
                <Select.Content position="popper">
                  <Select.Item value="light">Light</Select.Item>
                  <Select.Item value="dark">Dark</Select.Item>
                </Select.Content>
              </Select.Root>
            </EditorField>

            {/* Show Overview Logo */}
            <EditorField label="Show Overview Logo">
              <div className="flex items-center gap-2">
                <Switch
                  size="3"
                  checked={activeTheme.id === 'hubert-default' ? true : (activeTheme.showOverviewLogo ?? false)}
                  onCheckedChange={(val) => updateTheme({ showOverviewLogo: val })}
                  disabled={activeTheme.id === 'hubert-default'}
                />
                <Text size="3">
                  {activeTheme.id === 'hubert-default' || activeTheme.showOverviewLogo ? 'Enabled' : 'Disabled'}
                </Text>
              </div>
            </EditorField>

            {/* Logo & Favicon */}
            <EditorField label="Logo & Favicon">
              <div className="flex gap-8">
                {/* Logo column */}
                <div className="flex flex-col items-center gap-2">
                  <div className="relative flex h-32 w-32 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-inner">
                    {(activeTheme.logo?.light ?? activeTheme.logo?.dark) ? (
                      <>
                        <img
                          src={activeTheme.logo?.light ?? activeTheme.logo?.dark}
                          alt=""
                          className="max-h-full max-w-full object-contain"
                        />
                        <div className="absolute top-1 left-1 rounded-(--radius-1) bg-white">
                          <IconButton
                            variant="soft"
                            color="red"
                            size="1"
                            onClick={() => updateTheme({ logo: { light: '', dark: '' } })}
                            title="Remove logo"
                            className={activeTheme.id === 'hubert-default' ? '' : 'cursor-pointer!'}
                            disabled={activeTheme.id === 'hubert-default'}
                          >
                            <Trash2 size={12} />
                          </IconButton>
                        </div>
                      </>
                    ) : (
                      <Upload size={32} color="gray" />
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                  <Button
                    variant="outline"
                    disabled={activeTheme.id === 'hubert-default'}
                    onClick={() => fileInputRef.current?.click()}
                    className={activeTheme.id === 'hubert-default' ? '' : 'cursor-pointer!'}
                  >
                    <Upload size={16} /> Upload Logo
                  </Button>
                </div>

                {/* Favicon column */}
                <div className="flex flex-col items-center gap-2">
                  <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-inner">
                    {(activeTheme.favicon?.light ?? activeTheme.favicon?.dark) ? (
                      <>
                        <img
                          src={activeTheme.favicon?.light ?? activeTheme.favicon?.dark}
                          alt=""
                          className="max-h-full max-w-full object-contain"
                        />
                        <div className="absolute top-1 left-1 rounded-(--radius-1) bg-white">
                          <IconButton
                            variant="soft"
                            color="red"
                            size="1"
                            onClick={() => updateTheme({ favicon: { light: '', dark: '' } })}
                            title="Remove favicon"
                            disabled={activeTheme.id === 'hubert-default'}
                            className={activeTheme.id === 'hubert-default' ? '' : 'cursor-pointer!'}
                          >
                            <Trash2 size={12} />
                          </IconButton>
                        </div>
                      </>
                    ) : (
                      <Upload size={20} color="gray" />
                    )}
                  </div>
                  <input
                    type="file"
                    ref={faviconInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleFaviconUpload}
                  />
                  <Button
                    variant="outline"
                    disabled={activeTheme.id === 'hubert-default'}
                    onClick={() => faviconInputRef.current?.click()}
                    className={activeTheme.id === 'hubert-default' ? '' : 'cursor-pointer!'}
                  >
                    <Upload size={16} /> Upload Favicon
                  </Button>
                </div>
              </div>
            </EditorField>

            <EditorField label="Apply to Templates">
              {isLoadingTemplates ? (
                <Text size="2" color="gray">
                  Loading templates...
                </Text>
              ) : (
                <div className={activeTheme.id === 'hubert-default' ? 'pointer-events-none opacity-50' : ''}>
                  <MultiSelect
                    key={baselineKey(activeTheme.id, preSelectedIds)}
                    options={templateOptions}
                    preSelected={selectedTemplateIds}
                    onChange={(ids) => updateTheme({ templateIds: ids })}
                    accessibility={{
                      announceChanges: true,
                      placeholder: 'Select templates...',
                      ariaLabel: 'Templates to apply theme to',
                      allSelectedMessage: 'All templates selected',
                      getSelectedMessage: (label, count) => `${label} selected, ${count} total`,
                      getRemovedMessage: (label, count) => `${label} removed, ${count} remaining`,
                      getRemoveButtonLabel: (label) => `Remove ${label}`,
                    }}
                  />
                </div>
              )}
            </EditorField>

            <EditorField label="Apply to Campaigns">
              {isLoadingCampaigns ? (
                <Text size="2" color="gray">
                  Loading campaigns...
                </Text>
              ) : (
                <div className={activeTheme.id === 'hubert-default' ? 'pointer-events-none opacity-50' : ''}>
                  <MultiSelect
                    key={baselineKey(activeTheme.id, preSelectedCampaignIds)}
                    options={campaignOptions}
                    preSelected={selectedCampaignIds}
                    onChange={(ids) => updateTheme({ campaignIds: ids })}
                    accessibility={{
                      announceChanges: true,
                      placeholder: 'Select campaigns...',
                      ariaLabel: 'Campaigns to apply theme to',
                      allSelectedMessage: 'All campaigns selected',
                      getSelectedMessage: (label, count) => `${label} selected, ${count} total`,
                      getRemovedMessage: (label, count) => `${label} removed, ${count} remaining`,
                      getRemoveButtonLabel: (label) => `Remove ${label}`,
                    }}
                  />
                </div>
              )}
            </EditorField>

            <EditorField label="Apply to Jobs">
              {isLoadingJobs ? (
                <Text size="2" color="gray">
                  Loading jobs...
                </Text>
              ) : (
                <div className={activeTheme.id === 'hubert-default' ? 'pointer-events-none opacity-50' : ''}>
                  <MultiSelect
                    key={baselineKey(activeTheme.id, preSelectedJobIds)}
                    options={jobOptions}
                    preSelected={selectedJobIds}
                    onChange={(ids) => updateTheme({ jobIds: ids })}
                    accessibility={{
                      announceChanges: true,
                      placeholder: 'Select jobs...',
                      ariaLabel: 'Jobs to apply theme to',
                      allSelectedMessage: 'All jobs selected',
                      getSelectedMessage: (label, count) => `${label} selected, ${count} total`,
                      getRemovedMessage: (label, count) => `${label} removed, ${count} remaining`,
                      getRemoveButtonLabel: (label) => `Remove ${label}`,
                    }}
                  />
                </div>
              )}
            </EditorField>

            {/* Apply to Client */}
            <EditorField label="Apply to Client">
              <div className="flex items-center gap-2">
                <Switch
                  size="3"
                  checked={activeTheme.applyToClient}
                  disabled={activeTheme.id === 'hubert-default'}
                  onCheckedChange={(val) => updateTheme({ applyToClient: val })}
                />
                <Text size="3">{activeTheme.applyToClient ? 'Enabled' : 'Disabled'} for client</Text>
              </div>
            </EditorField>

            <div className="flex justify-end pt-10">
              <Button
                size="3"
                onClick={saveTheme}
                loading={isSaving}
                disabled={activeTheme.id === 'hubert-default'}
                className={`${activeTheme.id === 'hubert-default' ? '' : 'cursor-pointer!'} px-8`}
              >
                <Text weight="bold">Save</Text>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="flex w-[450px] flex-col items-center justify-center border-l border-gray-200 bg-gray-100 p-10 text-[10px] shadow-inner">
        <div className="relative aspect-9/19.5 w-full max-w-95">
          <ChatPreview theme={activeTheme} />
        </div>
      </div>
    </div>
  );
};
